import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useStore } from '../../context/StoreContext';
import { Card, CardHeader, CardTitle, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';
import { Scales, FloppyDisk } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LEGAL_PAGES = [
  { key: 'terms',   label: 'Conditions Générales de Vente (CGV)', hint: 'Obligatoires pour toute vente en ligne.' },
  { key: 'legal',   label: 'Mentions légales',                     hint: 'Obligatoires : SIRET, hébergeur, contact...' },
  { key: 'privacy', label: 'Politique de confidentialité (RGPD)',  hint: 'Requise si vous collectez des données personnelles.' },
  { key: 'cookies', label: 'Politique de cookies',                 hint: 'Obligatoire si vous utilisez des cookies non-essentiels.' },
  { key: 'returns', label: 'Conditions de retour / rétractation',  hint: 'Délai de 14 jours minimum en France.' },
];

const LegalPage = () => {
  const { currentStore } = useStore();
  const [data, setData] = useState({ terms: '', legal: '', privacy: '', cookies: '', returns: '' });
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    if (!currentStore) return;
    try {
      const { data: store } = await axios.get(`${API_URL}/api/stores/${currentStore.id}`, { withCredentials: true });
      setData(prev => ({ ...prev, ...(store?.legal || {}) }));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [currentStore]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.put(`${API_URL}/api/stores/${currentStore.id}`, { legal: data }, { withCredentials: true });
      toast.success('Documents légaux enregistrés');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally { setSaving(false); }
  };

  if (loading) return <div className="p-12 text-center text-slate-400">Chargement…</div>;

  return (
    <div className="space-y-5 max-w-4xl" data-testid="legal-page">
      <div>
        <p className="text-[10px] md:text-xs uppercase tracking-widest text-slate-400 font-semibold">Légal</p>
        <h1 className="text-2xl md:text-3xl font-black tracking-tight text-slate-900">Documents légaux</h1>
        <p className="text-sm text-slate-500 mt-1">Chaque document est publié automatiquement sur une page dédiée du site (ex. <code>/page/cgv</code>).</p>
      </div>

      {LEGAL_PAGES.map(page => (
        <Card key={page.key}>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2"><Scales size={18} />{page.label}</CardTitle>
            <p className="text-[11px] text-slate-500">{page.hint}</p>
          </CardHeader>
          <CardContent>
            <Label className="text-xs">Contenu (HTML / Markdown)</Label>
            <Textarea
              value={data[page.key] || ''}
              onChange={(e) => setData(prev => ({ ...prev, [page.key]: e.target.value }))}
              rows={10}
              placeholder="Rédigez le contenu ici…"
              className="font-mono text-xs"
              data-testid={`legal-${page.key}`}
            />
          </CardContent>
        </Card>
      ))}

      <div className="sticky bottom-3 flex justify-end">
        <Button onClick={save} disabled={saving} className="bg-[#0052FF] shadow-lg gap-2" data-testid="legal-save">
          <FloppyDisk size={16} /> {saving ? 'Enregistrement…' : 'Enregistrer'}
        </Button>
      </div>
    </div>
  );
};

export default LegalPage;
